import { SelectField, useFormContext, useFormField } from '@calendly/form';
import { FieldError, useErrorId } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { useClose } from '@calendly/ui/components/dropdown';
import { ArrowChevronLeftMiniIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { TextInput } from '@calendly/ui/components/text-input';
import { LabelText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { isNoTouchDevice } from 'services/touch_device_detection';
import { useErrorAfterInitialTouch } from '../hooks';
import { formatValuesToServerData } from './format_values_to_server_data';
const durationActionsCss = "d1ixqmlj";
const DurationActions = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(durationActionsCss, props.className)} />;
});
const inputContainerCss = "ile28nx";
const InputContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(inputContainerCss, props.className)} />;
});
export const CustomDurationField = ({
  defaultDuration,
  durationOptions,
  updateDurationOptions
}) => {
  const closeDropdown = useClose();
  const [customDurationFieldProps, customDurationMeta] = useFormField({
    name: 'tempCustomEventDuration'
  });
  const [customDurationTypeFieldProps] = useFormField({
    name: 'tempCustomEventDurationType'
  });
  const customDurationErrorId = useErrorId({
    tempCustomDurationError: customDurationMeta.error
  });
  const {
    setFieldValue,
    setFieldError
  } = useFormContext();
  const hideCustomDuration = () => setFieldValue('isCustomDurationOpen', false);
  useErrorAfterInitialTouch('tempCustomEventDuration');
  const unitSelectOptions = [{
    id: 'minutes',
    name: I18n.t('time.min_abbr')
  }, {
    id: 'hours',
    name: I18n.t('time.hours_abbr.one')
  }];
  const onClose = () => {
    hideCustomDuration();
    closeDropdown();
  };
  const addCustomDuration = () => {
    if (customDurationMeta.error) return;
    const customEventDuration = customDurationFieldProps.value;
    const customEventDurationType = customDurationTypeFieldProps.value;
    const {
      duration
    } = formatValuesToServerData({
      eventDuration: 'custom',
      customEventDuration,
      customEventDurationType
    });
    if (!duration) {
      onClose();
      return;
    }
    if (durationOptions.includes(duration) || duration === defaultDuration) {
      setFieldError('tempCustomEventDuration', I18n.t('publisher.event_types.availability.duration_fieldset.errors.duplicate'));
    } else {
      updateDurationOptions(duration);
      closeDropdown();
    }
  };
  return <>
      <MinimalButton spacingTop={8} spacingBottom={8} fontSize="small" onClick={hideCustomDuration}>
        <ArrowChevronLeftMiniIcon />
        {I18n.t('publisher.event_types.availability.duration_fieldset.all_durations')}
      </MinimalButton>
      <LabelText size="medium">
        {I18n.t('publisher.event_types.availability.duration_fieldset.add_custom_duration')}
      </LabelText>
      <InputContainer>
        <TextInput {...customDurationFieldProps} aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_value_label')} aria-invalid={customDurationMeta.touched && !!customDurationMeta.error} aria-describedby={customDurationErrorId} inputMode="numeric" autoFocus={isNoTouchDevice()} />
        <SelectField name="tempCustomEventDurationType" aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_unit_label')} options={unitSelectOptions} />
      </InputContainer>
      <FieldError text={customDurationMeta.error} id={customDurationErrorId} />
      <DurationActions>
        <Button decoration="secondary-outline" size="small" onClick={onClose}>
          {I18n.t('publisher.action.cancel')}
        </Button>
        <Button size="small" disabled={!!customDurationMeta.error} onClick={addCustomDuration}>
          {I18n.t('publisher.action.apply')}
        </Button>
      </DurationActions>
    </>;
};

require("./custom_duration_field.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./custom_duration_field.js");